import ProductLocationsApiV2 from 'api/v2/admins/product-locations-api-v2'
import CreatableSelect from 'react-select/creatable'
import OutboundInternalRequestsApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import TitlePage from 'components/atoms/title-page'
import InternalRequestDetail from 'domains/internal-request/organisms/internal-request-detail'
import PropTypes from 'prop-types'
import { useParams } from 'react-router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ALERT_TYPES } from 'constants/alert-constants'
import { Button, Card, CardBody, CardHeader, CardTitle } from 'reactstrap'
import InternalRequestItemTable from 'domains/internal-request/organisms/item-table'
import { ITEM_STATUS } from 'domains/internal-request/constants'
import RejectItemForm from 'domains/internal-request-ops/organisms/reject-item-form'
import InternalRequestService from 'domains/internal-request/services'
import withLoader from 'hocs/with-loader'
import CardLoader from 'components/organisms/card-loader'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const InternalRequestDetailOutboundOpsPage = ({ pageUtils }) => {
  const [ internalRequest, setInternalRequest ]               = useState({})
  const [ internalRequestLoading, setInternalRequestLoading ] = useState(true)
  const [ items, setItems ]                                   = useState({
    pending  : [],
    picked   : [],
    rejected : []
  })
  const [ itemsLoading, setItemsLoading ]                     = useState({
    pending  : false,
    picked   : false,
    rejected : false
  })
  const [ itemsPagination, setItemsPagination ]               = useState({
    pending  : {},
    picked   : {},
    rejected : {}
  })
  const [ modalOpen, setModalOpen ]                           = useState({
    rejectModal : false
  })
  const [ itemToReject, setItemToReject ]                     = useState({})
  const [ selectedPendingRows, setSelectedPendingRows ]       = useState({})
  const [ dispatchLocations, setDispatchLocations ] = useState({})
  const [ locationOptions, setLocationOptions ] = useState([])
  const [ dispatchBusy, setDispatchBusy ] = useState(false)
  useEffect(() => {
    ProductLocationsApiV2.get({ type_name: 'Lokasi Item' })
      .then((response) => setLocationOptions((response.data.product_locations || []).filter((location) => location.active !== false).map((location) => ({ value: location.id, label: location.label || location.name }))))
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data || error.message))
  }, [ pageUtils ])
  const dispatchPayload = (item) => {
    const chosen = dispatchLocations[item.id]
    return chosen?.__isNew__
      ? { dispatch_location_name: chosen.label }
      : { dispatch_product_location_id: chosen?.value }
  }
  const renderDispatchLocation = (item) => (
    <div style={{ minWidth: 220 }}>
      <CreatableSelect isClearable isDisabled={ dispatchBusy }
        aria-label={ `Lokasi dispatch ${item.inventory?.inventory_sku || item.id}` }
        options={ locationOptions } value={ dispatchLocations[item.id] || null }
        onChange={ (value) => setDispatchLocations((current) => ({ ...current, [item.id]: value })) }
        placeholder='Lokasi tujuan (opsional)'
        formatCreateLabel={ (name) => `Buat lokasi: ${name}` }
        menuPortalTarget={ document.body } styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
      <small>{ item.inventory?.product?.item_location_label || item.inventory?.inventory_locations?.[0]?.label || 'Lokasi sekarang mengikuti Inventory' }</small>
    </div>
  )
  const { id }                                                = useParams()

  useEffect(() => {
    // show internal request endpoint
    OutboundInternalRequestsApiV2.show(id)
      .then((response) => {
        setInternalRequest(response.data.internal_request)
        setInternalRequestLoading(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ pageUtils, id ])

  /** all methods for fetching internal request items */
  const handleFetchPendingItems = async (tableState) => {
    setItemsLoading((prevState) => ({ ...prevState, pending: true }))
    const additionalFilters = {
      status: [ ITEM_STATUS.PENDING ]
    }
    await OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      additionalFilters   : additionalFilters,
      tableState          : tableState
    })
      .then((response) => {
        setItems((prevState) => ({ ...prevState, pending: response.data.internal_request_items }))
        setItemsPagination((prevState) => ({ ...prevState, pending: response.data.meta }))
      })
      .catch(() => pageUtils.setAlertMsg(
        'fetching pending items error'
        , ALERT_TYPES.DANGER
      ))
      .finally(() => setItemsLoading((prevState) => ({ ...prevState, pending: false })) )
  }

  const handleFetchPickedItems = async (tableState) => {
    setItemsLoading((prevState) => ({ ...prevState, picked: true }))
    const additionalFilters = {
      status: [ ITEM_STATUS.PICKED ]
    }
    await OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      additionalFilters   : additionalFilters,
      tableState          : tableState
    })
      .then((response) => {
        setItems((prevState) => ({ ...prevState, picked: response.data.internal_request_items }))
        setItemsPagination((prevState) => ({ ...prevState, picked: response.data.meta }))
      })
      .catch(() => pageUtils.setAlertMsg(
        'fetching pending items error'
        , ALERT_TYPES.DANGER
      ))
      .finally(() => setItemsLoading((prevState) => ({ ...prevState, picked: false })) )
  }

  const handleFetchRejectedItems = async (tableState) => {
    setItemsLoading((prevState) => ({ ...prevState, rejected: true }))
    const additionalFilters = {
      status: [ ITEM_STATUS.REJECTED ]
    }
    await OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      additionalFilters   : additionalFilters,
      tableState          : tableState
    })
      .then((response) => {
        setItems((prevState) => ({ ...prevState, rejected: response.data.internal_request_items }))
        setItemsPagination((prevState) => ({ ...prevState, rejected: response.data.meta }))
      })
      .catch(() => pageUtils.setAlertMsg(
        'fetching rejected items error'
        , ALERT_TYPES.DANGER
      ))
      .finally(() => setItemsLoading((prevState) => ({ ...prevState, rejected: false })) )
  }
  /** end of methods for fetching internal request items */

  /** methods for processing items */
  const refreshPickAndSendTables = () => {
    setSelectedPendingRows({})
    handleFetchPendingItems()
    handleFetchPickedItems()
  }

  const pickLocationIdForItem = (item) => {
    const locations = item.inventory?.inventory_locations || []
    const location = locations[0]

    return location?.location_id || location?.id || ''
  }

  const handlePickAndSendItem = (datum) => {
    if (dispatchBusy) return
    setDispatchBusy(true)
    OutboundInternalRequestsApiV2.pickAndSendItem({
      internal_request_id : internalRequest.id,
      item_id             : datum.id,
      location_id         : pickLocationIdForItem(datum),
      quantity            : datum.quantity,
      ...dispatchPayload(datum)
    })
      .then(() => {
        pageUtils.setAlertMsg('Item berhasil di-Pick & Send.', ALERT_TYPES.SUCCESS)
        refreshPickAndSendTables()
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data || error.message))
      .finally(() => setDispatchBusy(false))
  }

  const handlePickAndSendAll = () => {
    if (dispatchBusy) return
    const selectedItems = items.pending.filter((item) => selectedPendingRows[item.id])

    if (!selectedItems.length) {
      pageUtils.setAlertMsg('Pilih minimal satu item untuk Pick & Send.', ALERT_TYPES.WARNING)
      return
    }

    setDispatchBusy(true)
    OutboundInternalRequestsApiV2.pickAndSendAll({
      internal_request_id : internalRequest.id,
      items               : selectedItems.map((item) => ({
        id                       : item.id,
        quantity                 : item.quantity,
        tl_warehouse_location_id : pickLocationIdForItem(item),
        ...dispatchPayload(item)
      }))
    })
      .then(() => {
        pageUtils.setAlertMsg('Selected items berhasil di-Pick & Send.', ALERT_TYPES.SUCCESS)
        refreshPickAndSendTables()
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data || error.message))
      .finally(() => setDispatchBusy(false))
  }

  const handleRejectItem = (values, formikBag) => {
    formikBag.setSubmitting(true)
    console.log(values)
    OutboundInternalRequestsApiV2.rejectItem({
      internal_request_id : internalRequest.id,
      item_id             : itemToReject.id,
      reject_reason       : values.reject_reason,
      reject_note         : values.reject_note,
      quantity            : values.quantity
    })
      .then(() => {
        setModalOpen((prevState) => ({
          ...prevState,
          rejectModal: false
        }))
        setItemToReject({})
        handleFetchPendingItems()
        handleFetchRejectedItems()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  /** end of methods for processing items */

  const isPickDisabled = useCallback((datum) => {
    const storedQuantity = datum?.inventory?.stored_quantity
    const borrowedQuantity = InternalRequestService.getQuantityToBorrow(storedQuantity)

    return borrowedQuantity <= 0 ? true : false
  }, [])

  /** methods for opening and closing modal */
  const handleOpenRejectModal = useCallback((datum) => {
    setModalOpen((prevState) => ({
      ...prevState,
      rejectModal: true
    }))
    setItemToReject(datum)
  }, [])

  const handleCloseRejectModal =() => {
    setModalOpen((prevState) => ({
      ...prevState,
      rejectModal: false
    }))
    setItemToReject({})
  }

  /** end of methods for opening and closing modal */

  /** methods for printing picking items list */
  const printPickingItems = async () => {
    try {
      const response = await OutboundInternalRequestsApiV2.downloadPickingItemsPdf(internalRequest.id)
      const blob = new Blob([ response.data ], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      const contentDisposition = response.headers?.['content-disposition'] || ''
      const matchedFilename = contentDisposition.match(/filename="?([^";]+)"?/)

      link.href = url
      link.download = matchedFilename?.[1] || `${internalRequest.code || 'internal_request'}_handover_form.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || error.message)
    }
  }

  const InternalRequestDetailCardWithLoader = withLoader(InternalRequestDetail, CardLoader)

  /** items table hidden columns */
  const pendingItemsHiddenColumns = [
    'status',
    'marked_for_return',
    'reject_reason',
    'reject_note',
    'quantity',
    'quantity_put_away',
    'borrowed',
    'returned',
    'unreturned'
  ]

  const pickedItemsHiddenColumns = [
    'status',
    'marked_for_return',
    'reject_reason',
    'reject_note',
    'quantity',
    'quantity_put_away',
    'quantity_picked',
    'quantity_to_borrow',
    'quantity_in_warehouse',
    'borrowed',
    'returned',
    'unreturned'
  ]

  const rejectedItemsHiddenColumns = [
    'status',
    'marked_for_return',
    'quantity',
    'quantity_put_away',
    'quantity_picked',
    'quantity_to_borrow',
    'quantity_in_warehouse',
    'location',
    'borrowed',
    'returned',
    'unreturned'
  ]

  const rowButtonProps = useMemo(() => [
    {
      header     : 'Pick & Send',
      id         : 'action_pick',
      text       : 'Pick & Send',
      colour     : 'primary',
      isDisabled : (item) => dispatchBusy || isPickDisabled(item),
      onClick    : handlePickAndSendItem
    },
    {
      header  : 'Reject',
      id      : 'action_reject',
      text    : 'Reject',
      colour  : 'danger',
      onClick : handleOpenRejectModal
    }
  ], [ isPickDisabled, handleOpenRejectModal, handlePickAndSendItem, dispatchBusy ])

  return (
    <>
      <TitlePage mainTitle={ 'Internal Request Ops New Request' } subTitle={ internalRequest.code } />

      <InternalRequestDetailCardWithLoader
        isLoading={ internalRequestLoading }
        internal_request={ internalRequest }
      />

      { /* Pending Items Table */ }
      <Card>
        <CardHeader>
          <CardTitle className="text-default font-weight-bolder">Pending Items</CardTitle>
        </CardHeader>
        <CardBody>
          <Button
            className="mb-3 mr-2"
            color='primary'
            onClick={ () => printPickingItems() }
          >
            Print Handover Form
          </Button>
          <Button
            className="mb-3"
            color='success'
            disabled={ dispatchBusy }
            onClick={ handlePickAndSendAll }
          >
            Pick & Send All
          </Button>
          <div className='mb-2 text-muted small'>Pilih atau ketik lokasi tujuan per item. Lokasi baru dibuat saat Pick & Send; SQ/IQ tetap. Kosongkan untuk mempertahankan lokasi saat ini.</div>
          <InternalRequestItemTable
            renderLocation={ renderDispatchLocation }
            data={ items.pending }
            pagination={ itemsPagination.pending }
            isLoading={ itemsLoading.pending }
            onFetchData={ handleFetchPendingItems }
            hiddenColumns={ pendingItemsHiddenColumns }
            rowButtonProps={ rowButtonProps }
            isAbleToSelectRow
            selectedRows={ selectedPendingRows }
            onSetSelectedRows={ setSelectedPendingRows }
          />
        </CardBody>
      </Card>

      { /* Picked Items Table */ }
      <Card>
        <CardHeader>
          <CardTitle className="text-default font-weight-bolder">Picked & Sent Items</CardTitle>
        </CardHeader>
        <CardBody>
          <InternalRequestItemTable
            data={ items.picked }
            pagination={ itemsPagination.picked }
            isLoading={ itemsLoading.picked }
            onFetchData={ handleFetchPickedItems }
            hiddenColumns={ pickedItemsHiddenColumns }
          />
        </CardBody>
      </Card>

      { /* Rejected Items Table */ }
      <Card>
        <CardHeader>
          <CardTitle className="text-default font-weight-bolder">Rejected Items</CardTitle>
        </CardHeader>
        <CardBody>
          <InternalRequestItemTable
            data={ items.rejected }
            pagination={ itemsPagination.rejected }
            isLoading={ itemsLoading.rejected }
            onFetchData={ handleFetchRejectedItems }
            hiddenColumns={ rejectedItemsHiddenColumns }
          />
        </CardBody>
      </Card>

      { /* Reject Modal Form */ }
      <Modal
        isOpen={  modalOpen.rejectModal }
        isClosed={ handleCloseRejectModal }
        size='lg'
      >
        <ModalHeader>
          { `Reject Item ${itemToReject.inventory?.inventory_sku}` }
        </ModalHeader>
        <ModalBody>
          <RejectItemForm
            internalRequestItem={ itemToReject }
            handleSubmit={ handleRejectItem }
            onCancelButtonClick={ handleCloseRejectModal }
          />
        </ModalBody>
      </Modal>
    </>
  )
}

InternalRequestDetailOutboundOpsPage.propTypes = propTypes

export default InternalRequestDetailOutboundOpsPage
